import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowRight,
  FaCheckCircle,
  FaClock,
  FaBookOpen,
  FaCertificate,
  FaStar,
  FaPlayCircle,
  FaMobileAlt,
  FaDownload,
  FaGlobe,
  FaCode,
  FaLock,
  FaBolt,
} from "react-icons/fa";

const DCMachinesEnroll = () => {
  const navigate = useNavigate();

  const [submitted, setSubmitted] = useState(false);
  const [studentName, setStudentName] = useState("");
  const [isFooterVisible, setIsFooterVisible] = useState(false);

  const footerMarkerRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsFooterVisible(entry.isIntersecting);
      },
      { rootMargin: "0px 0px -20px 0px" }
    );

    if (footerMarkerRef.current) {
      observer.observe(footerMarkerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();

    const name = e.target.fullName.value;

    setStudentName(name);

    setTimeout(() => {
      setSubmitted(true);
      e.target.reset();
    }, 700);
  };

  return (
    <div className="relative overflow-hidden text-slate-200 min-h-screen bg-[#050816]">

      {/* ================= PREMIUM BACKGROUND ================= */}

      <div className="absolute inset-0 bg-gradient-to-br from-black via-[#0b0f19] to-[#111827] opacity-95"></div>

      {/* Glow Waves */}
      <div className="absolute inset-0 opacity-40 pointer-events-none">

        <div
          className="absolute top-0 left-0 w-[120%] h-[120%]
          bg-gradient-to-r from-orange-500/30 via-yellow-400/20 to-sky-500/30
          blur-3xl animate-slowWave"
        ></div>

        <div
          className="absolute bottom-0 right-0 w-[120%] h-[120%]
          bg-gradient-to-r from-sky-500/20 via-orange-300/20 to-yellow-400/30
          blur-3xl animate-fastWave"
        ></div>

      </div>

      {/* GRID */}
      <div
        className="absolute inset-0 pointer-events-none
        bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),
             linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)]
        bg-[size:60px_60px]"
      ></div>

      {/* Floating Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">

        {[...Array(20)].map((_, i) => (
          <span
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full opacity-20 animate-pulse"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDuration: `${2 + Math.random() * 4}s`,
            }}
          ></span>
        ))}

      </div>

      {/* ================= SUCCESS PAGE ================= */}

      {submitted ? (
        <div className="relative z-20 flex items-center justify-center min-h-screen px-4 py-20">

          <div className="max-w-3xl w-full rounded-[2.5rem] border border-white/10 bg-[#121826]/90 backdrop-blur-2xl p-10 md:p-14 text-center shadow-[0_0_50px_rgba(255,196,0,0.08)]">

            <div className="w-28 h-28 mx-auto rounded-full bg-green-500/15 border border-green-400/30 flex items-center justify-center mb-8 animate-pulse">
              <FaCheckCircle className="text-6xl text-green-400" />
            </div>

            <h2
              className="text-5xl font-black mb-4
              bg-gradient-to-r from-orange-300 via-yellow-200 to-cyan-300
              text-transparent bg-clip-text animate-gradientMove bg-[length:200%_200%]"
            >
              Enrollment Successful
            </h2>

            <p className="text-2xl text-slate-300 mt-4">
              Welcome{" "}
              <span className="text-yellow-300 font-bold">
                {studentName}
              </span>
            </p>

            <div className="mt-10 grid md:grid-cols-2 gap-5 text-left">

              {[
                ["Course", "DC Machines Master Program"],
                ["Duration", "3 Months Intensive Training"],
                ["Batch Timing", "7:00 PM - 8:30 PM"],
                ["Course Access", "Within 24 Hours"],
              ].map((item, index) => (
                <div
                  key={index}
                  className="rounded-3xl border border-white/10 bg-[#121826]/80 p-6 hover:border-yellow-400/20 hover:-translate-y-1 transition-all duration-500"
                >
                  <p className="text-slate-400 text-sm">{item[0]}</p>

                  <h3 className="text-xl font-bold mt-2 text-white">
                    {item[1]}
                  </h3>
                </div>
              ))}

            </div>

            <button
              onClick={() => navigate("/training")}
              className="mt-12 px-10 py-4 rounded-2xl bg-gradient-to-r from-orange-500 via-yellow-400 to-orange-500 text-black font-bold hover:scale-105 transition-all duration-500"
            >
              Back to Training
            </button>

          </div>

        </div>
      ) : (
        <>
          {/* ================= MASTER LAYOUT ================= */}

          <div className="relative z-10 max-w-7xl mx-auto w-full px-4 md:px-10 pt-10 pb-24 grid lg:grid-cols-[1fr_420px] gap-16 items-start">

            {/* ================= LEFT CONTENT ================= */}

            <div className="space-y-24 pt-10">

              {/* ================= HERO SECTION ================= */}

              <section className="pt-20">

                {/* TOP TAGS */}
                <div className="flex flex-wrap gap-4 mb-8">

                  <div className="px-5 py-2 rounded-full border border-orange-400/20 bg-[#121826]/80 backdrop-blur-xl text-orange-200 text-sm font-semibold shadow-lg shadow-orange-500/10 animate-pulse">
                    Bestseller Course
                  </div>

                  <div className="px-5 py-2 rounded-full border border-cyan-400/20 bg-[#121826]/80 backdrop-blur-xl text-cyan-200 text-sm font-semibold shadow-lg shadow-cyan-500/10 animate-pulse">
                    100% Practical Training
                  </div>

                </div>

                {/* TITLE */}
                <h1
                  className="
                    text-5xl md:text-6xl xl:text-7xl
                    font-black leading-[0.95]
                    tracking-tight mb-6
                    bg-gradient-to-r
                    from-orange-300
                    via-yellow-200
                    to-cyan-300
                    text-transparent bg-clip-text
                    animate-gradientMove
                    bg-[length:200%_200%]
                  "
                >
                  DC Machines
                  <br />
                  Complete Training
                  <br />
                  Program
                </h1>

                {/* DESCRIPTION */}
                <p className="mt-8 text-slate-300 text-lg md:text-xl leading-relaxed max-w-2xl">
                  Master DC Machines from fundamentals to advanced industrial
                  applications with real-time practical sessions,
                  industrial projects and certification training.
                </p>

                {/* STATS */}
                <div className="flex flex-wrap gap-10 mt-12">

                  <div className="hover:scale-105 transition-all duration-500">

                    <div className="flex items-center gap-2 text-yellow-300 text-lg">
                      <FaStar />
                      <FaStar />
                      <FaStar />
                      <FaStar />
                      <FaStar />
                    </div>

                    <p className="mt-3 text-xl font-black text-white">
                      4.9 Rating
                    </p>

                    <p className="text-slate-400">
                      500+ Reviews
                    </p>

                  </div>

                  <div className="hover:scale-105 transition-all duration-500">

                    <p className="text-4xl font-black bg-gradient-to-r from-orange-300 to-yellow-200 bg-clip-text text-transparent">
                      1200+
                    </p>

                    <p className="text-slate-400 mt-2">
                      Students Enrolled
                    </p>

                  </div>

                  <div className="hover:scale-105 transition-all duration-500">

                    <p className="text-4xl font-black bg-gradient-to-r from-cyan-300 to-blue-200 bg-clip-text text-transparent">
                      3 Months
                    </p>

                    <p className="text-slate-400 mt-2">
                      Live Training
                    </p>

                  </div>

                </div>

                {/* FEATURE CARDS */}
                <div className="grid sm:grid-cols-2 gap-6 mt-16 max-w-2xl">

                  <div
                    className="
                      group rounded-[2rem]
                      border border-orange-400/20
                      bg-[#121826]/80 backdrop-blur-2xl
                      p-7
                      hover:-translate-y-2
                      transition-all duration-700
                      hover:shadow-[0_0_40px_rgba(249,115,22,0.20)]
                    "
                  >

                    <div className="flex items-center gap-5">

                      <div className="w-16 h-16 rounded-2xl bg-orange-500/20 flex items-center justify-center text-orange-300 text-2xl group-hover:rotate-12 transition-all duration-700">
                        <FaClock />
                      </div>

                      <div>
                        <h3 className="text-xl font-black text-white">
                          45+ Hours
                        </h3>

                        <p className="text-slate-300 mt-1">
                          Recorded + Live Classes
                        </p>
                      </div>

                    </div>

                  </div>

                  <div
                    className="
                      group rounded-[2rem]
                      border border-cyan-400/20
                      bg-[#121826]/80 backdrop-blur-2xl
                      p-7
                      hover:-translate-y-2
                      transition-all duration-700
                      hover:shadow-[0_0_40px_rgba(34,211,238,0.20)]
                    "
                  >

                    <div className="flex items-center gap-5">

                      <div className="w-16 h-16 rounded-2xl bg-cyan-500/20 flex items-center justify-center text-cyan-300 text-2xl group-hover:rotate-12 transition-all duration-700">
                        <FaCertificate />
                      </div>

                      <div>
                        <h3 className="text-xl font-black text-white">
                          Certification
                        </h3>

                        <p className="text-slate-300 mt-1">
                          Industry Recognized
                        </p>
                      </div>

                    </div>

                  </div>

                </div>

              </section>

              {/* ================= WHAT YOU WILL LEARN ================= */}

              <section>

                <div
                  className="
                    rounded-[2.5rem]
                    border border-white/10
                    bg-[#121826]/70
                    backdrop-blur-2xl
                    p-10 md:p-14
                    hover:border-yellow-400/20
                    hover:-translate-y-1
                    transition-all duration-500
                    hover:shadow-[0_0_40px_rgba(255,196,0,0.08)]
                  "
                >

                  <div className="flex items-center gap-3 mb-12">

                    <FaBolt className="text-yellow-400 text-2xl" />

                    <h2
                      className="
                        text-4xl font-black
                        bg-gradient-to-r from-orange-300 to-cyan-300
                        bg-clip-text text-transparent
                      "
                    >
                      What You'll Learn
                    </h2>

                  </div>

                  <div className="grid md:grid-cols-2 gap-6">

                    {[
                      "Understand complete DC Machines concepts",
                      "Learn generators and motors",
                      "Industrial troubleshooting methods",
                      "Live practical implementation",
                      "Speed & torque analysis",
                      "Real-time industrial projects",
                      "Electrical maintenance systems",
                      "Job interview preparation",
                    ].map((item, index) => (
                      <div
                        key={index}
                        className="
                          flex items-start gap-4
                          rounded-3xl
                          border border-white/10
                          bg-[#121826]/80 backdrop-blur-2xl
                          p-5
                          hover:bg-orange-500/10
                          hover:shadow-[0_0_30px_rgba(255,196,0,0.08)]
                          transition-all duration-500
                        "
                      >

                        <div className="min-w-[34px] min-h-[34px] rounded-full bg-green-500/20 flex items-center justify-center">
                          <FaCheckCircle className="text-green-400 text-sm" />
                        </div>

                        <p className="text-slate-300">
                          {item}
                        </p>

                      </div>
                    ))}

                  </div>

                </div>

              </section>

              {/* ================= COURSE CONTENT ================= */}

              <section>

                <div
                  className="
                    rounded-[2.5rem]
                    border border-white/10
                    bg-[#121826]/70
                    backdrop-blur-2xl
                    p-10 md:p-14
                    hover:border-cyan-400/20
                    transition-all duration-500
                  "
                >

                  <h2
                    className="
                      text-4xl font-black mb-4
                      bg-gradient-to-r from-cyan-300 to-blue-200
                      bg-clip-text text-transparent
                    "
                  >
                    Course Content
                  </h2>

                  <p className="text-slate-400 mb-10">
                    12 Modules • 85 Lectures • 45+ Hours Total Duration
                  </p>

                  <div className="space-y-5">

                    {[
                      "Introduction to DC Machines",
                      "Construction & Working Principle",
                      "EMF Equation & Characteristics",
                      "DC Generators",
                      "DC Motors",
                      "Speed Control Methods",
                      "Losses & Efficiency",
                      "Applications & Industrial Uses",
                    ].map((course, index) => (
                      <div
                        key={index}
                        className="
                          rounded-3xl
                          border border-white/10
                          bg-[#121826]/80
                          hover:bg-cyan-500/10
                          hover:shadow-[0_0_40px_rgba(255,196,0,0.10)]
                          transition-all duration-500
                          p-6
                        "
                      >

                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">

                          <div className="flex items-center gap-5">

                            <div className="w-12 h-12 rounded-2xl bg-orange-500/10 border border-orange-400/20 flex items-center justify-center text-orange-300 font-bold">
                              {index + 1}
                            </div>

                            <h3 className="text-xl font-bold text-white">
                              {course}
                            </h3>

                          </div>

                          <div className="text-slate-400">
                            10 Lectures • 3h 20min
                          </div>

                        </div>

                      </div>
                    ))}

                  </div>

                </div>

              </section>

            </div>

            {/* ================= STICKY RIGHT CARD ================= */}

            <aside className="hidden lg:block w-[420px] relative min-h-full self-stretch">

              <div
                className={`
                  w-[420px]
                  rounded-[2.5rem]
                  overflow-hidden
                  border border-white/10
                  bg-[#121826]/95 backdrop-blur-2xl
                  shadow-[0_0_80px_rgba(255,255,255,0.08)]
                  hover:shadow-[0_0_80px_rgba(249,115,22,0.15)]
                  transition-all duration-300
                  max-h-[calc(100vh-9rem)]
                  overflow-y-auto
                  custom-scrollbar
                  z-30
                  ${
                    isFooterVisible
                      ? "absolute bottom-0"
                      : "fixed top-28"
                  }
                `}
              >

                {/* IMAGE */}
                <div className="relative overflow-hidden">

                  <img
                    src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?q=80&w=1200&auto=format&fit=crop"
                    alt="course"
                    className="w-full h-[240px] object-cover hover:scale-110 transition-all duration-1000"
                  />

                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">

                    <button className="w-24 h-24 rounded-full bg-white/90 flex items-center justify-center text-5xl text-orange-500 hover:scale-110 transition-all duration-500 shadow-2xl animate-pulse">
                      <FaPlayCircle />
                    </button>

                  </div>

                </div>

                {/* CONTENT */}
                <div className="p-8">

                  <div className="flex items-center gap-4 mb-8">

                    <h2 className="text-5xl font-black text-white">
                      ₹999
                    </h2>

                    <span className="text-slate-400 line-through text-xl">
                      ₹2999
                    </span>

                  </div>

                  <button

                    onClick={() => navigate("/payment")}
                    className="
                      w-full py-5 rounded-2xl
                      bg-gradient-to-r
                      from-orange-500
                      via-yellow-400
                      to-orange-500
                      text-black font-black text-lg
                      hover:scale-[1.03]
                      transition-all duration-500
                      shadow-[0_10px_40px_rgba(249,115,22,0.35)]
                    "
                  >
                    Subscribe Now
                  </button>

                  <div className="space-y-5 mt-8 text-slate-300">

                    <div className="flex items-center gap-4">
                      <FaBookOpen className="text-orange-400" />
                      <span>45+ Hours Complete Training</span>
                    </div>

                    <div className="flex items-center gap-4">
                      <FaMobileAlt className="text-orange-400" />
                      <span>Mobile & Laptop Access</span>
                    </div>

                    <div className="flex items-center gap-4">
                      <FaDownload className="text-orange-400" />
                      <span>Downloadable Notes & PDFs</span>
                    </div>

                    <div className="flex items-center gap-4">
                      <FaGlobe className="text-orange-400" />
                      <span>Lifetime Access</span>
                    </div>

                    <div className="flex items-center gap-4">
                      <FaCode className="text-orange-400" />
                      <span>Industrial Projects</span>
                    </div>

                  </div>

                </div>

              </div>

            </aside>

          </div>

          {/* FOOTER MARKER */}
          <div
            ref={footerMarkerRef}
            className="w-full h-1 pointer-events-none"
          />
        </>
      )}

      {/* ================= ANIMATIONS ================= */}

      <style>{`

        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.15);
          border-radius: 4px;
        }

        @keyframes slowWave {
          0% {
            transform: translate(-10%, -10%) rotate(0deg);
          }

          50% {
            transform: translate(10%, 10%) rotate(14deg);
          }

          100% {
            transform: translate(-10%, -10%) rotate(0deg);
          }
        }

        @keyframes fastWave {
          0% {
            transform: translate(10%, 10%) rotate(0deg);
          }

          50% {
            transform: translate(-10%, -10%) rotate(-14deg);
          }

          100% {
            transform: translate(10%, 10%) rotate(0deg);
          }
        }

        @keyframes gradientMove {
          0% {
            background-position: 0% 50%;
          }

          50% {
            background-position: 100% 50%;
          }

          100% {
            background-position: 0% 50%;
          }
        }

        .animate-gradientMove {
          animation: gradientMove 6s ease infinite;
        }

        .animate-slowWave {
          animation: slowWave 14s ease-in-out infinite;
        }

        .animate-fastWave {
          animation: fastWave 10s ease-in-out infinite;
        }

      `}</style>

    </div>
  );
};

export default DCMachinesEnroll;